/**
 * Path to Performance Payload Mapping Utilities
 * Transforms action items data to API payload format
 */

import type { 
  ActionItemData, 
  P2PSavePayload,
  CategoryLevelsResponse,
} from "../../store/slices/pathToPerformanceSlice";
import { findActionId } from "./pathToPerformanceHelpers";

/**
 * Category field mapping for API payload
 * Maps category names to their corresponding API field names
 */
const CATEGORY_FIELD_MAP = {
  Sponsorship: {
    level: "sponsershipCatLvl" as const,
    actionId: "sponsershipActionId" as const,
    notes: "sponsershipNotes" as const,
  },
  Leadership: {
    level: "leadershipCatLvl" as const,
    actionId: "leadershipActionId" as const,
    notes: "leadershipNotes" as const,
  },
  Training: {
    level: "trainingCatLvl" as const,
    actionId: "trainingActionId" as const,
    notes: "trainingNotes" as const,
  },
  "Team Process": {
    level: "processCatLvl" as const,
    actionId: "processActionId" as const,
    notes: "processNotes" as const,
  },
  "Team Engagement": {
    level: "teamEngCatLvl" as const,
    actionId: "teamEngActionId" as const,
    notes: "teamEngNotes" as const,
  },
  "Use of Tools": {
    level: "toolsUseCatLvl" as const,
    actionId: "toolsUseActionId" as const,
    notes: "toolsUseNotes" as const,
  },
  "Goals and Performance": {
    level: "goalsPerfCatLvl" as const,
    actionId: "goalsPerfActionId" as const,
    notes: "goalsPerfNotes" as const,
  },
} as const;

/**
 * Map a single action item to API payload fields
 * 
 * @param item - Action item data
 * @param categoryLevels - Category levels from API
 * @param payload - Payload object to update
 * @throws Error if category is invalid
 */
export const mapSingleActionItem = (
  item: ActionItemData,
  categoryLevels: CategoryLevelsResponse | null,
  payload: Partial<P2PSavePayload>,
): void => {
  const fieldMap = CATEGORY_FIELD_MAP[item.category as keyof typeof CATEGORY_FIELD_MAP];
  
  if (!fieldMap) {
    // Throw error instead of silent warn for better error tracking
    const validCategories = Object.keys(CATEGORY_FIELD_MAP).join(', ');
    throw new Error(
      `Invalid category: "${item.category}". Expected one of: ${validCategories}`
    );
  }

  const level = item.categoryLevel === 0 ? null : item.categoryLevel;
  const actionId = findActionId(item.category, item.actionNeeded, categoryLevels);
  const levelValue = typeof level === "number" ? level : null;
  const notes = item.notes || null;

  payload[fieldMap.level] = levelValue;
  payload[fieldMap.actionId] = actionId;
  payload[fieldMap.notes] = notes;
};

/**
 * Map all action items to API payload
 * 
 * @param actionItems - Array of action item data
 * @param categoryLevels - Category levels from API
 * @returns Partial P2P save payload
 * 
 * @example
 * ```typescript
 * const payload = mapActionItemsToPayload(actionItems, categoryLevels);
 * ```
 */
export const mapActionItemsToPayload = (
  actionItems: ActionItemData[],
  categoryLevels: CategoryLevelsResponse | null,
): Partial<P2PSavePayload> => {
  const payload: Partial<P2PSavePayload> = {};
  
  actionItems.forEach((item) => {
    mapSingleActionItem(item, categoryLevels, payload);
  });
  
  return payload;
};
